import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Button, Icon, Input, Spinner, useToast } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import "./accept-invite.css";

/**
 * Redeeming an invitation.
 *
 * This is the only unauthenticated route besides sign-in, and the only way a
 * colleague can get an account — invitations were being minted with no way to
 * accept them, so every new person had to be created by hand on the server.
 *
 * Accepting signs you straight in: having just set a password, being bounced
 * to a login form to type it again is a pointless step.
 */

type InvitationSummary = {
  email: string;
  invitedByName: string;
  expiresAt: string;
};

const MIN_PASSWORD = 12;

export function AcceptInvite() {
  usePageTitle("Accept invitation");
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { refresh } = useAuth();

  const [invitation, setInvitation] = useState<InvitationSummary | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);

  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ invitation: InvitationSummary }>(`/auth/invitation/${encodeURIComponent(token)}`)
      .then((res) => {
        if (cancelled) return;
        setInvitation(res.invitation);
      })
      .catch(() => !cancelled && setInvalid(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const mismatch = confirm.length > 0 && password !== confirm;
  const tooShort = password.length > 0 && password.length < MIN_PASSWORD;
  const canSubmit =
    displayName.trim().length > 0 && password.length >= MIN_PASSWORD && password === confirm && !submitting;

  const submit = useCallback(() => {
    if (!canSubmit) return;
    setSubmitting(true);
    api
      .post("/auth/invitation/accept", {
        token,
        displayName: displayName.trim(),
        password,
      })
      .then(async () => {
        // Accepting an invitation signs the new person in with a session
        // cookie; re-read the session to pick it up.
        await refresh();
        navigate("/home", { replace: true });
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not set up that account.", { tone: "danger" });
        setSubmitting(false);
      });
  }, [canSubmit, token, displayName, password, refresh, navigate, toast]);

  if (loading) {
    return (
      <div className="accept">
        <div className="accept__card accept__card--centred">
          <Spinner />
          <p>Checking that invitation…</p>
        </div>
      </div>
    );
  }

  if (invalid || !invitation) {
    return (
      <div className="accept">
        <div className="accept__card">
          <Brand />
          <h1>This invitation is no longer valid.</h1>
          <p className="accept__muted">
            It may have expired, already been used, or been revoked. Ask whoever invited you to send a new one.
          </p>
          <Button variant="secondary" onClick={() => navigate("/login")}>
            Go to sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="accept">
      <form
        className="accept__card"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Brand />

        <h1>Set up your account</h1>
        <p className="accept__muted">
          <strong>{invitation.invitedByName}</strong> invited <strong>{invitation.email}</strong> to Astrum Connect.
        </p>

        <Input
          label="Your name"
          placeholder="How you want to appear to colleagues"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          autoFocus
          required
        />

        <Input
          label="Password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint={`At least ${MIN_PASSWORD} characters. A memorable phrase beats a short, complicated one.`}
          error={tooShort ? `Use at least ${MIN_PASSWORD} characters.` : undefined}
          required
        />

        <Input
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={mismatch ? "These do not match." : undefined}
          required
        />

        <Button type="submit" fullWidth loading={submitting} disabled={!canSubmit}>
          Create account
        </Button>

        <p className="accept__note">
          <Icon name="lock" size={12} />
          Your access and security events are recorded.
        </p>
      </form>
    </div>
  );
}

function Brand() {
  return (
    <div className="accept__brand">
      <span className="accept__mark">A</span>
      <span className="accept__wordmark">
        ASTRUM<strong>CONNECT</strong>
      </span>
    </div>
  );
}
